import React, { useEffect, useState } from 'react';
import mammoth from 'mammoth';
import './AttachmentPreview.css';

const API_BASE = process.env.REACT_APP_API_BASE;

// Helper: build data URL if content_bytes or data_url provided
function base64ToBlobUrl(base64, mime) {
  const byteString = atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
  const blob = new Blob([ab], { type: mime });
  return URL.createObjectURL(blob);
}

function base64ToArrayBuffer(base64) {
  const byteString = atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
  return ab;
}

const contentEndpoint = (messageId, attachmentId, encoded = true) =>
  encoded
    ? `${API_BASE}/attachments/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}/content`
    : `${API_BASE}/attachments/messages/${messageId}/attachments/${attachmentId}/content`;

async function fetchWithFallbackContent(messageId, attachmentId, opts = {}) {
  const encodedUrl = contentEndpoint(messageId, attachmentId, true);
  console.debug('Fetching attachment content (percent-encoded):', encodedUrl);
  const resp = await fetch(encodedUrl, opts);
  return resp;
}

export default function AttachmentPreview({ messageId, attachment, onClose }) {
  const [url, setUrl] = useState(null);
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    const createdUrls = [];
    setLoading(true);
    setError(null);
    setUrl(null);
    setHtml(null);

    async function load() {
      try {
        // If backend already included data_url or content_bytes, use it.
        if (attachment.data_url) {
          if (!active) return;

          // Normalize data URLs: detect base64 data and prefer creating a blob URL
          const m = attachment.data_url.match(/^data:(.*?);base64,(.*)$/);
          if (m) {
            let mime = m[1] || (attachment.content_type || 'application/octet-stream');
            const b64 = m[2];

            // If server returned octet-stream but filename indicates pdf/docx, fix mime
            const name = (attachment.name || '').toLowerCase();
            if (mime === 'application/octet-stream') {
              if (name.endsWith('.pdf')) mime = 'application/pdf';
              else if (name.endsWith('.docx')) mime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            }

            // If docx: try to convert to HTML for inline preview via mammoth
            if (mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || (name.endsWith('.docx') && !mime.includes('pdf'))) {
              try {
                const ab = base64ToArrayBuffer(b64);
                const result = await mammoth.convertToHtml({ arrayBuffer: ab });
                if (!active) return;
                setHtml(result.value);
                return;
              } catch (err) {
                // fall back to blob URL below
              }
            }

            // For PDFs and other previewable types create blob URL so iframe can render
            const blobUrl = base64ToBlobUrl(b64, mime);
            createdUrls.push(blobUrl);
            if (!active) return;
            setUrl(blobUrl);
            return;
          }

          // If it's already a data: URL that does not match above, just use it
          if (!active) return;
          setUrl(attachment.data_url);
          return;
        }

        if (attachment.content_bytes) {
          const mime = attachment.content_type || 'application/octet-stream';
          const blobUrl = base64ToBlobUrl(attachment.content_bytes, mime);
          createdUrls.push(blobUrl);
          if (!active) return;
          setUrl(blobUrl);
          return;
        }

        // Fetch content from content endpoint (binary or json with base64)
        const acceptHeader = attachment.content_type
          ? `${attachment.content_type}, application/octet-stream, */*`
          : 'application/octet-stream, application/json, */*';

        const resp = await fetchWithFallbackContent(messageId, attachment.id, { headers: { Accept: acceptHeader } });

        if (!resp.ok) {
          const text = await resp.text().catch(()=>null);
          throw new Error(`Failed to fetch attachment content (${resp.status}) ${text || ''}`);
        }

        const contentType = resp.headers.get('content-type') || '';

        if (contentType.includes('application/json')) {
          const json = await resp.json();
          const att = json.attachment || json;
          if (att.data_url) {
            // reuse the data_url handling above by temporarily assigning
            attachment = { ...attachment, data_url: att.data_url, content_type: att.content_type || attachment.content_type, name: att.name || attachment.name };
            // recall load to handle
            return load();
          }
          if (att.content_bytes) {
            const mime = att.content_type || attachment.content_type || 'application/octet-stream';
            // try docx->html
            if (mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || (attachment.name || '').toLowerCase().endsWith('.docx')) {
              try {
                const ab = base64ToArrayBuffer(att.content_bytes);
                const result = await mammoth.convertToHtml({ arrayBuffer: ab });
                if (!active) return;
                setHtml(result.value);
                return;
              } catch (err) {
                // fallback to blob url
              }
            }
            const blobUrl = base64ToBlobUrl(att.content_bytes, mime);
            createdUrls.push(blobUrl);
            if (!active) return;
            setUrl(blobUrl);
            return;
          }
          throw new Error('No inline content available in JSON response');
        } else {
          // binary response
          const ab = await resp.arrayBuffer();
          const mime = attachment.content_type || contentType.split(';')[0] || 'application/octet-stream';
          const blob = new Blob([ab], { type: mime });
          const blobUrl = URL.createObjectURL(blob);
          createdUrls.push(blobUrl);

          if (mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || (attachment.name || '').toLowerCase().endsWith('.docx')) {
            try {
              const result = await mammoth.convertToHtml({ arrayBuffer: ab });
              if (!active) return;
              setHtml(result.value);
              setUrl(null);
              return;
            } catch (err) {
              // fallback to blob URL
            }
          }

          if (!active) return;
          setUrl(blobUrl);
        }
      } catch (err) {
        if (!active) return;
        console.error('Attachment preview error:', err);
        setError(err.message || 'Preview failed');
      } finally {
        if (!active) return;
        setLoading(false);
      }
    }

    load();

    return () => {
      active = false;
      // revoke any created blob urls
      createdUrls.forEach(u => {
        try { URL.revokeObjectURL(u); } catch(e){/*ignore*/ }
      });
    };
  }, [attachment, messageId]);

  async function download() {
    try {
      // if we already have a URL (blob or data url) use it
      if (url && url.startsWith('blob:')) {
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.name || 'attachment';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }

      // fetch endpoint and handle json/binary as in load()
  const resp = await fetchWithFallbackContent(messageId, attachment.id, { headers: { Accept: 'application/json, */*' } });
      if (!resp.ok) throw new Error('Failed to download attachment');

      const contentType = resp.headers.get('content-type') || '';

      if (contentType.includes('application/json')) {
        const json = await resp.json();
        const att = json.attachment || json;
        if (att.content_bytes) {
          const mime = att.content_type || attachment.content_type || 'application/octet-stream';
          const blob = await (async () => {
            const b = atob(att.content_bytes);
            const ab = new ArrayBuffer(b.length);
            const ia = new Uint8Array(ab);
            for (let i = 0; i < b.length; i++) ia[i] = b.charCodeAt(i);
            return new Blob([ab], { type: mime });
          })();
          const u = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = u;
          a.download = attachment.name || 'attachment';
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(u);
          return;
        }
        throw new Error('No binary in JSON response');
      } else {
        const ab = await resp.arrayBuffer();
        const mime = attachment.content_type || contentType.split(';')[0] || 'application/octet-stream';
        const blob = new Blob([ab], { type: mime });
        const u = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = attachment.name || 'attachment';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(u);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Download failed');
    }
  }

  const isPdf = (attachment.content_type === 'application/pdf') || (attachment.name || '').toLowerCase().endsWith('.pdf');

  return (
    <div className="av-modal-overlay">
      <div className="av-modal">
        <div className="av-modal-header">
          <strong className="av-modal-title">{attachment.name}</strong>
          <div>
            <button className="av-btn" onClick={download}>Download</button>
            <button className="av-btn av-btn-ghost" onClick={onClose}>Close</button>
          </div>
        </div>

        <div className="av-modal-body">
          {loading && <div className="av-loading">Preparing preview…</div>}
          {error && <div className="av-error">{error}</div>}
          {html && <div className="av-html-preview" dangerouslySetInnerHTML={{ __html: html }} />}
          {!html && isPdf && url && (
            <iframe title="pdf-preview" src={url} className="av-iframe" />
          )}
          {!html && !isPdf && url && (
            <iframe title="file-preview" src={url} className="av-iframe" />
          )}
          {!html && !url && !loading && <div className="av-no-preview">No preview available. Use download.</div>}
        </div>
      </div>
    </div>
  );
}